import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { PostCardContent_Annotation } from '../../PostCardContent/PostCardContent'
import { PostCardNotes_Note } from './PostCardNotes_Note'
import { PostMediaAsset_Asset } from '../PostMedia/PostMediaAsset'

export const PostCard_Post = graphql(
  `
    fragment PostCard_Post on Post {
      __typename
      id
      # Group membership and title only (batched on the backend) — sibling
      # posts are loaded by PostGroupDialog when it opens, never by cards
      # (CORE-5605).
      postGroup {
        id
        title
      }
      isProcessing @client
      allowedActions
      ideaId
      status
      notificationStatus
      sharedNow
      via
      schedulingType
      author {
        __typename
        id
        email
        name
        avatar
        isDeleted
      }
      isCustomScheduled
      isPinned
      externalLink
      createdAt
      updatedAt
      dueAt
      sentAt
      metricsUpdatedAt
      text
      externalLink
      metadata {
        __typename
        ... on CommonPostMetadata {
          type
          annotations {
            ...PostCardContent_Annotation
          }
        }
        ... on InstagramPostMetadata {
          audio {
            id
            audioVolume
            videoVolume
          }
          firstComment
          link
          geolocation {
            id
            text
          }
          isAiGenerated
        }
        ... on PinterestPostMetadata {
          title
          url
          board {
            serviceId
            name
            url
            description
            avatar
          }
        }
        ... on GoogleBusinessPostMetadata {
          title
        }
        ... on TwitterPostMetadata {
          threadCount
          thread {
            text
            assets {
              __typename
              ...PostMediaAsset_Asset
            }
          }
          retweet {
            id
            url
            text
            createdAt
            user {
              name
              username
              avatar
            }
          }
          isAiGenerated
        }
        ... on TiktokPostMetadata {
          isAiGenerated
        }
        ... on YoutubePostMetadata {
          isAiGenerated
        }
        ... on LinkedInPostMetadata {
          firstComment
          linkAttachment {
            __typename
            url
            expandedUrl
            title
            thumbnail
            text
          }
        }
        ... on FacebookPostMetadata {
          firstComment
          linkAttachment {
            __typename
            url
            expandedUrl
            title
            thumbnail
            text
          }
        }
        ... on ThreadsPostMetadata {
          threadCount
          thread {
            text
            assets {
              __typename
              ...PostMediaAsset_Asset
            }
          }
          topic
          locationId
          locationName
          linkAttachment {
            __typename
            url
            expandedUrl
            title
            thumbnail
            text
          }
        }
        ... on MastodonPostMetadata {
          threadCount
          thread {
            text
            assets {
              __typename
              ...PostMediaAsset_Asset
            }
          }
        }
        ... on BlueskyPostMetadata {
          threadCount
          thread {
            text
            assets {
              __typename
              ...PostMediaAsset_Asset
            }
          }
          linkAttachment {
            __typename
            url
            expandedUrl
            title
            thumbnail
            text
          }
        }
        ... on SubstackPostMetadata {
          linkAttachment {
            __typename
            url
            expandedUrl
            title
            thumbnail
            text
          }
        }
      }
      channel {
        __typename
        id
        allowedActions
        type
        name
        avatar
        service
        apiVersion
        products
        serviceId
        serverUrl
        timezone
        displayName
        isQueuePaused
        isDisconnected
        locationData {
          location
        }
        metadata {
          __typename
          ... on TwitterMetadata {
            subscriptionType
          }
          ... on BlueskyMetadata {
            serverUrl
          }
          ... on MastodonMetadata {
            serverUrl
            maxCharacters
          }
        }
        scopes
      }
      tags {
        id
        name
        color
      }
      notes {
        ...PostCardNotes_Note
      }
      error {
        message
        supportUrl
      }
      assets {
        __typename
        ...PostMediaAsset_Asset
      }
      metrics {
        type
        name
        description
        value
        nullableValue
        unit
      }
      recurrence {
        cadence {
          frequency
          frequencyStep
        }
        maxRecurrentPosts
        recurrentPostsLeft
        postNumber
        parentPostId
      }
    }
  `,
  [PostCardContent_Annotation, PostMediaAsset_Asset, PostCardNotes_Note],
)

export type PostCard_PostData = ResultOf<typeof PostCard_Post>
